import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

const LAST_UPDATED = "November 24, 2025";

const definitionList = [
  {
    term: "Provider",
    description:
      "A healthcare professional or organization using Anthro Care to manage or monitor patients.",
  },
  {
    term: "Designee",
    description:
      "A staff member, assistant, or third party authorized by a Provider to use the platform.",
  },
  {
    term: "Patient",
    description: "An individual receiving care services through the platform.",
  },
  {
    term: "PHI",
    description: "Protected Health Information as defined under HIPAA.",
  },
  {
    term: "Platform",
    description:
      "Anthro Care’s web portal, app, monitoring tools, communication tools, surveys, alerts, and analytics systems.",
  },
];

const sections = [
  {
    id: "introduction",
    number: "1.",
    title: "Introduction",
    body: [
      "Welcome to Anthro Care, a Private Limited Company (“Anthro Care,” “we,” “us,” or “our”). These Terms & Conditions (“Terms”) govern your access to and use of our website, platform, mobile applications, and all related healthcare technology services (collectively, the “Services”).",
      "By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, you must not use the Services.",
    ],
  },
  {
    id: "scope",
    number: "3.",
    title: "Scope of Services",
    body: [
      "Anthro Care provides a secure healthcare technology platform that may include:",
    ],
    bullets: [
      "Remote patient monitoring (RPM)",
      "Health assessments, surveys, and check-ins",
      "Monitoring through smart devices",
      "Messaging, reminders, and automated alerts",
      "Data analytics and insights",
      "Uploading medical photos/videos (e.g., wound images)",
      "Provider-patient communication tools",
      "Care coordination features",
    ],
    footer:
      "These Services are intended to support — not replace — the professional judgment and treatment plan of licensed healthcare Providers.",
  },
  {
    id: "emergency",
    number: "4.",
    title: "Not Emergency Services",
    body: [
      "Anthro Care IS NOT an emergency service. If you are experiencing an emergency or urgent medical crisis, call your local emergency number immediately.",
      "The platform must never be relied upon for emergency decision-making, life-supporting critical alerts, or time-sensitive medical responses.",
    ],
  },
  {
    id: "medical-advice",
    number: "5.",
    title: "No Medical Advice",
    body: [
      "Anthro Care does not provide medical advice, diagnose, or treat conditions, and does not replace professional clinical judgment. All medical decisions should be made by Providers. Patients must consult their Provider for any health concerns.",
    ],
  },
  {
    id: "enrollment",
    number: "6.",
    title: "Enrollment & Consent",
    subSections: [
      {
        heading: "6.1 Patient Authorization",
        body: [
          "Patients enroll through a Provider’s invitation or care program. By enrolling, Patients consent for Anthro Care to collect, store, analyze, and transmit PHI; communicate with Providers and Designees; send health-related reminders and messages; use PHI to improve platform functionality; and provide analytics, alerts, and suggestions to Providers.",
        ],
      },
      {
        heading: "6.2 HIPAA Authorization",
        body: [
          "Anthro Care operates as a Business Associate under HIPAA. As part of enrollment, Patients authorize the use and disclosure of PHI for treatment and care coordination, healthcare operations, quality improvement, clinical suggestions, communication with Providers, device integration, data analysis, reporting, platform improvements, and expressly consented marketing or educational updates.",
        ],
      },
      {
        heading: "6.3 Revocation of Authorization",
        body: [
          "Patients may revoke authorization at any time by contacting support@anthro.care. Revocation does not affect prior actions taken while authorization was valid.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    number: "7.",
    title: "Data Privacy, Security & Cookies",
    subSections: [
      {
        heading: "7.1 Security Standards",
        body: [
          "Anthro Care maintains strict administrative, physical, and technical safeguards including HIPAA-compliant encryption, role-based access control, secure coding practices, vulnerability testing, monitoring, encrypted backups, disaster recovery protocols, and audit logs.",
        ],
      },
      {
        heading: "7.2 Cookies & Tracking",
        body: [
          "We use essential cookies (required for authentication), analytics cookies (usage tracking), and performance or preference cookies. Disabling cookies may impact certain features.",
        ],
      },
      {
        heading: "7.3 Third-Party Services",
        body: [
          "Anthro Care may engage partners for cloud hosting, analytics, customer service, payments, and device integrations. Any partner handling PHI operates under a Business Associate Agreement where required.",
        ],
      },
    ],
  },
  {
    id: "responsibilities",
    number: "8.",
    title: "User Responsibilities",
    body: [
      "By using the Services, Users agree to provide accurate information, maintain confidentiality of credentials, refrain from sharing access, use the platform lawfully, avoid reverse engineering or reproducing the platform, prevent harm or disruption, and ensure the security of their devices.",
    ],
  },
  {
    id: "ip",
    number: "9.",
    title: "Intellectual Property",
    body: [
      "All software, content, trademarks, UI/UX, text, images, code, databases, analytics, and platform features belong exclusively to Anthro Care. Users receive a limited, non-transferable, revocable license to access the Services. Unauthorized reproduction or derivative work is prohibited.",
    ],
  },
  {
    id: "billing",
    number: "10.",
    title: "Payment & Billing (If Applicable)",
    body: [
      "If any Service requires fees, you agree to pay all applicable charges. Payments are non-refundable unless expressly stated. Failure to pay may result in suspension or termination, and pricing may change with notice.",
    ],
  },
  {
    id: "liability",
    number: "11.",
    title: "Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, Anthro Care is not liable for indirect, incidental, or consequential damages; lost revenue, data, or business interruption; medical decisions made by Providers; third-party services or integrations; device data issues; user errors or misinterpretations; service interruptions; or technical issues.",
      "Any maximum liability shall not exceed the total fees paid by you in the 12 months preceding the claim.",
    ],
  },
  {
    id: "termination",
    number: "12.",
    title: "Termination",
    subSections: [
      {
        heading: "12.1 By User",
        body: [
          "You may stop using the Services at any time. To delete your account, contact support@anthro.care.",
        ],
      },
      {
        heading: "12.2 By Anthro Care",
        body: [
          "We may suspend or terminate access for security threats, violation of Terms, fraud or misuse, legal requirements, or non-payment (if applicable).",
        ],
      },
      {
        heading: "12.3 Data Handling After Termination",
        body: [
          "Data may be retained for legal compliance, audit purposes, and healthcare regulatory requirements. Deletion of PHI follows our Privacy Policy.",
        ],
      },
    ],
  },
  {
    id: "changes",
    number: "13.",
    title: "Changes to Terms",
    body: [
      "We may update or revise these Terms at any time. Changes will be posted with a new Last Updated date. Continued use of the Services constitutes acceptance of the updated Terms.",
    ],
  },
  {
    id: "law",
    number: "14.",
    title: "Governing Law",
    body: [
      "These Terms are governed by the laws of the State of Wyoming, USA, without regard to conflict-of-law principles. Any disputes must be brought to state or federal courts in Wyoming.",
    ],
  },
  {
    id: "misc",
    number: "15.",
    title: "Miscellaneous",
    bullets: [
      "Severability: If any provision is invalid, the remainder remains enforceable.",
      "Waiver: Failure to enforce a provision is not a waiver of that right.",
      "Assignment: Users may not assign rights; Anthro Care may assign in mergers or acquisitions.",
      "Entire Agreement: These Terms plus our Privacy Policy constitute the full agreement.",
    ],
  },
  {
    id: "contact",
    number: "16.",
    title: "Contact Us",
    body: [
      "If you have questions about these Terms or your rights, contact:",
      "Anthro Care\n30 N Gould St Ste 100\nSheridan, WY 82801, USA\n📧 support@anthro.care",
    ],
  },
];

export default function Terms() {
  return (
    <DashboardLayout activePage="/terms">
      <div className="space-y-6 sm:space-y-8">
        <header className="space-y-4">
          <Badge variant="secondary" className="text-xs uppercase tracking-wide">
            Legal
          </Badge>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Terms & Conditions
            </h1>
            <p className="text-muted-foreground mt-2 max-w-3xl">
              These Terms govern your access to and use of all Anthro Care
              services, applications, and tools. Please review them carefully.
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            Last Updated: <span className="font-medium">{LAST_UPDATED}</span>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-[280px,1fr]">
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-base">Quick Navigation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <nav className="space-y-2 text-sm">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block rounded-md px-3 py-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  >
                    {section.number} {section.title}
                  </a>
                ))}
              </nav>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-10 pt-6">
              <section id="definitions" className="space-y-4">
                <h2 className="text-2xl font-semibold">
                  2. Definitions (Key Terms)
                </h2>
                <div className="grid gap-4 rounded-lg border p-4 md:grid-cols-2">
                  {definitionList.map((item) => (
                    <div key={item.term}>
                      <p className="text-sm font-semibold">{item.term}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <Separator />

              <ScrollArea className="h-[65vh] pr-4">
                <div className="space-y-10">
                  {sections.map((section) => (
                    <section key={section.id} id={section.id} className="space-y-4">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-primary">
                          {section.number}
                        </span>
                        <h3 className="text-xl font-semibold">{section.title}</h3>
                      </div>
                      {section.body?.map((paragraph) => (
                        <p key={paragraph} className="text-sm leading-relaxed text-muted-foreground">
                          {paragraph}
                        </p>
                      ))}
                      {section.bullets && (
                        <ul className="list-disc space-y-2 pl-6 text-sm text-muted-foreground">
                          {section.bullets.map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                      {section.subSections?.map((sub) => (
                        <div key={sub.heading} className="space-y-2 border-l-2 border-muted pl-4">
                          <h4 className="text-sm font-semibold text-foreground">
                            {sub.heading}
                          </h4>
                          {sub.body.map((paragraph) => (
                            <p key={paragraph} className="text-sm text-muted-foreground">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      ))}
                      {section.footer && (
                        <p className="text-sm font-medium text-muted-foreground">
                          {section.footer}
                        </p>
                      )}
                    </section>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

